<template>
    <ChatAgentToolFrame is-actionable :is-complete="action.action_state.submitted" class="w-full" data-testid="document-creator-widget">
        <div class="nadia-chat flex flex-col gap-1 md:w-[530px]">
            <div class="nadia-chat text-valence-primary-500">
                <div class="flex items-center justify-between">
                    {{ action.action_params.document.title }}
                </div>
            </div>
        </div>
        <div class="body-small text-valence-grey-750 whitespace-pre-line max-h-[400px] overflow-hidden" data-testid="document-creator-preview">{{ content }}</div>
        <div class="flex flex-col md:flex-row gap-[10px] justify-between w-full">
            <!-- TODO: BaseButton text size override will eventually be standardized with proper design system button -->
            <BaseButton
                :disabled="loading"
                theme="tertiary"
                class="flex justify-center items-center gap-[14px] w-full"
                data-testid="document-creator-preview-button"
                @click="showPreview = !showPreview">
                Preview full document
                <i class="bi bi-arrows-angle-expand text-[16px]" aria-hidden="true"></i>
            </BaseButton>
            <div class="flex flex-col md:flex-row gap-[10px] w-full">
                <BaseDropdownMenu
                    v-if="userDetails.can_receive_emails"
                    v-model:open="emailPopOverOpen"
                    :sections="emailFormatSections"
                    :side-offset="5"
                    @select="handleEmailSelect">
                    <template #trigger>
                        <BaseButton theme="tertiary" class="flex flex-row gap-[6px] w-full px-6 py-3! rounded-3xl! justify-center" :disabled="emailLoading">
                            <span v-if="emailLoading">Sending...</span>
                            <span v-else>Email</span>
                            <div v-if="emailPopOverOpen && !emailLoading">
                                <i class="bi bi-chevron-up text-[16px]" aria-hidden="true"></i>
                            </div>
                            <div v-else-if="!emailLoading">
                                <i class="bi bi-chevron-down text-[16px]" aria-hidden="true"></i>
                            </div>
                        </BaseButton>
                    </template>
                </BaseDropdownMenu>
                <BaseDropdownMenu v-model:open="popOverOpen" :sections="downloadFormatSections" :side-offset="5" @select="handleDownloadSelect">
                    <template #trigger>
                        <BaseButton theme="primary" class="flex flex-row gap-[6px] w-full px-6 py-3! rounded-3xl! justify-center" data-testid="document-creator-download-button">
                            Download
                            <div v-if="popOverOpen">
                                <i class="bi bi-chevron-up text-[16px]" aria-hidden="true"></i>
                            </div>
                            <div v-else>
                                <i class="bi bi-chevron-down text-[16px]" aria-hidden="true"></i>
                            </div>
                        </BaseButton>
                    </template>
                </BaseDropdownMenu>
            </div>
        </div>
    </ChatAgentToolFrame>
    <!-- Preview Modal -->
    <BaseDialog v-model:open="showPreview" title="Document preview">
        <div data-testid="document-creator-preview-modal" class="flex flex-col justify-start items-center w-full md:w-[65vw] max-w-[1050px] bg-[#FAFAFA] gap-[40px]">
            <!-- Preview Header -->
            <div class="flex flex-row justify-between items-center px-6 w-full border-b py-5">
                <div class="flex flex-row gap-2">
                    <BaseDropdownMenu :sections="emailFormatSections" align="start" :side-offset="2" @select="handleEmailPreviewSelect">
                        <template #trigger>
                            <BaseButton aria-label="Email document" theme="neutral" class="border-none" :disabled="emailLoading">
                                <i class="bi bi-envelope text-valence-grey-750 text-[24px]" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                    <BaseDropdownMenu :sections="downloadFormatSections" align="start" :side-offset="2" @select="handleDownloadPreviewSelect">
                        <template #trigger>
                            <BaseButton aria-label="Download document" theme="neutral" class="border-none">
                                <i class="bi bi-download text-valence-grey-750 text-[24px]" aria-hidden="true"></i>
                            </BaseButton>
                        </template>
                    </BaseDropdownMenu>
                </div>
                <h2 class="text-valence-grey-750 title-subsection">Preview</h2>
                <BaseButton aria-label="Close document preview" theme="neutral" class="border-none" @click="closePreview">
                    <i data-testid="preview-modal-close-button" class="bi bi-x text-[30px] text-valence-grey-750" aria-hidden="true"></i>
                </BaseButton>
            </div>
            <!-- Preview Body -->
            <div class="bg-white border-2 grow min-h-[80dvh] min-w-[90%] p-6 md:p-[72px] md:mb-[40px] mx-[40px]">
                <div v-if="fullContentLoading" class="text-valence-grey-750" data-testid="document-creator-full-content-loading">Loading full document…</div>
                <div v-else class="text-valence-grey-750 whitespace-pre-line" data-testid="document-creator-full-content">{{ previewContent }}</div>
            </div>
            <div class="px-4 absolute bottom-10 w-[90%]">
                <BaseNotification
                    v-if="showErrorNotification"
                    :text="errorMessage"
                    :theme="errorMessage.includes('successfully') ? 'success' : 'error'"
                    @dismiss="showErrorNotification = !showErrorNotification" />
            </div>
        </div>
    </BaseDialog>
</template>

<script setup>
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import truncate from "lodash/truncate";
import { computed, inject, ref, watch } from "vue";

import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const props = defineProps({
    action: {
        type: Object,
        required: true,
    },
    lineIdx: { type: Number, required: true },
    messageId: { type: Number, required: true },
});

const { emitter } = inject("globalProperties");
const userDetails = inject("user_details");

// Hard coding the supported formats for now
const downloadFormatOptions = ["PDF", "DOCX", "TXT", "XLSX"];

const formatSections = (icon) => [{ id: "formats", items: downloadFormatOptions.map((format) => ({ id: format.toLowerCase(), type: "button", label: format, icon })) }];
const emailFormatSections = formatSections("bi bi-envelope");
const downloadFormatSections = formatSections("bi bi-download");

// The API and analytics take the display casing ("PDF"), which is the item label.
const handleEmailSelect = (item) => emailDocument(item.label);
const handleDownloadSelect = (item) => downloadDocument(item.label);
const handleEmailPreviewSelect = (item) => emailAndClosePreview(item.label);
const handleDownloadPreviewSelect = (item) => downloadAndClosePreview(item.label);

const popOverOpen = ref(false);
const emailPopOverOpen = ref(false);
const showPreview = ref(false);
const coachingSessionUuid = inject("coachingSessionUuid");
const showErrorNotification = ref(false);
const errorMessage = ref("");
const emailLoading = ref(false);

const TRUNCATE_LENGTH = 500;

// For large documents the backend ships only a bounded preview inline (with content_truncated set)
// to keep the realtime payload small; the full content is fetched on demand for the preview modal.
const fullContent = ref(null);
const fullContentLoading = ref(false);

const isContentTruncated = computed(() => props.action.action_params.document.content_truncated === true);

const content = computed(() => {
    return truncate(props.action.action_params.document.content, {
        length: TRUNCATE_LENGTH,
        omission: " ...",
    });
});

const previewContent = computed(() => {
    if (isContentTruncated.value && fullContent.value !== null) {
        return fullContent.value;
    }
    return props.action.action_params.document.content;
});

async function fetchFullContent() {
    if (!isContentTruncated.value || fullContent.value !== null || fullContentLoading.value) {
        return;
    }
    fullContentLoading.value = true;
    const { data, execute, onFetchError, onFetchResponse } = useFetch(`/api/chat/document/content?chat_message=${props.messageId}`, { immediate: false }).get().json();

    onFetchResponse(() => {
        if (data.value?.content != null) {
            fullContent.value = data.value.content;
        }
    });
    onFetchError(() => {
        logError(new Error("Failed to fetch full document content for preview"));
    });

    await execute();
    fullContentLoading.value = false;
}

// Lazy-load the full document the first time the preview opens for a truncated document.
watch(showPreview, (open) => {
    if (open) {
        fetchFullContent();
    }
});

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === "string" ? reader.result : "";
            resolve(result.split(",")[1] ?? "");
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

function showError(message) {
    if (!showPreview.value) {
        emitter.emit("error_notification", message);
    } else {
        errorMessage.value = message;
        showErrorNotification.value = true;
    }
}

function downloadAndClosePreview(option) {
    downloadDocument(option, "preview");
}

function emailAndClosePreview(option) {
    emailDocument(option, "preview");
}

function closePreview() {
    showPreview.value = false;
}

async function downloadDocument(format, source = "tool_call") {
    const baseErrorMessage = "Failed to download document";

    const requestData = ref({
        requested_format: format.toLowerCase(),
        chat_message: props.messageId,
    });

    const { data, execute, onFetchError, onFetchResponse } = useFetch("/api/chat/document/create", { immediate: false }).post(requestData).blob();

    onFetchError(() => {
        showError(baseErrorMessage);
        logUserInteraction("document_download_errored", { format, source }, coachingSessionUuid);
    });

    onFetchResponse(async (response) => {
        try {
            const contentDisposition = response.headers.get("Content-Disposition");

            if (!contentDisposition) {
                logError(new Error("Content-Disposition header not found for document creation response"));
                throw new Error(baseErrorMessage);
            }

            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (!filenameMatch || !filenameMatch[1]) {
                logError(new Error("Filename not found in Content-Disposition header"));
                throw new Error(baseErrorMessage);
            }

            let filename = filenameMatch[1].replace(/['"]/g, "");

            if (Capacitor.isNativePlatform()) {
                // WKWebView (iOS) and Android WebView ignore <a download>. Write the
                // blob to the device filesystem and hand it to the native share sheet
                // so the user can save it to Files / Downloads / etc.
                const base64 = await blobToBase64(data.value);
                await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
                const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
                try {
                    await Share.share({ title: props.action.action_params.document.title, url: uri });
                } catch {
                    // User dismissed the share sheet — not a real failure.
                }
            } else {
                const downloadUrl = globalThis.URL.createObjectURL(data.value);
                const downloadLink = document.createElement("a");
                downloadLink.href = downloadUrl;
                downloadLink.download = filename;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                globalThis.URL.revokeObjectURL(downloadUrl);
            }

            popOverOpen.value = false;

            logUserInteraction("document_downloaded", { format, source }, coachingSessionUuid);
        } catch {
            showError(baseErrorMessage);
            logUserInteraction("document_download_errored", { format, source }, coachingSessionUuid);
        }
    });

    await execute();
}

async function emailDocument(format, source = "tool_call") {
    const baseErrorMessage = "Failed to email document";
    const successMessage = "Document emailed successfully! Check your inbox.";

    emailLoading.value = true;

    const requestData = ref({
        requested_format: format.toLowerCase(),
        chat_message: props.messageId,
    });

    const { data, execute, onFetchError, onFetchResponse, onFetchFinally } = useFetch("/api/chat/document/email", { immediate: false }).post(requestData).json();

    onFetchError(() => {
        showError(baseErrorMessage);
        logUserInteraction("document_email_errored", { format, source }, coachingSessionUuid);
    });

    onFetchResponse(() => {
        if (data.value?.success) {
            emailPopOverOpen.value = false;

            if (!showPreview.value) {
                emitter.emit("success_notification", successMessage);
            } else {
                errorMessage.value = successMessage;
                showErrorNotification.value = true;
            }

            logUserInteraction("document_emailed", { format, source }, coachingSessionUuid);
        } else {
            showError(data.value?.message || baseErrorMessage);
            logUserInteraction("document_email_errored", { format, source }, coachingSessionUuid);
        }
    });

    onFetchFinally(() => {
        emailLoading.value = false;
    });

    await execute();
}
</script>
